import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Circle, PlayCircle, CheckCircle } from 'lucide-react';
import { Task } from '@/types/product-manager';

interface TaskStatusTabsProps {
  tasks: Task[];
  activeTab: 'todo' | 'in-progress' | 'completed';
  onTabChange: (tab: 'todo' | 'in-progress' | 'completed') => void;
}

const TaskStatusTabs: React.FC<TaskStatusTabsProps> = ({
  tasks,
  activeTab,
  onTabChange
}) => {
  const getTabData = () => {
    const todoCount = tasks.filter(t => t.status === 'todo').length;
    const inProgressCount = tasks.filter(t => t.status === 'in-progress').length;
    const completedCount = tasks.filter(t => t.status === 'completed').length;

    return [
      {
        id: 'todo' as const,
        label: 'To Do',
        icon: Circle,
        iconColor: 'text-gray-500',
        count: todoCount,
        activeColor: 'border-gray-500 text-gray-600',
        hoverColor: 'hover:text-gray-600 hover:border-gray-300'
      },
      {
        id: 'in-progress' as const,
        label: 'In Progress',
        icon: PlayCircle,
        iconColor: 'text-blue-500',
        count: inProgressCount,
        activeColor: 'border-blue-500 text-blue-600',
        hoverColor: 'hover:text-blue-600 hover:border-blue-300'
      },
      {
        id: 'completed' as const,
        label: 'Completed',
        icon: CheckCircle,
        iconColor: 'text-green-500',
        count: completedCount,
        activeColor: 'border-green-500 text-green-600',
        hoverColor: 'hover:text-green-600 hover:border-green-300'
      }
    ];
  };

  const tabs = getTabData();

  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                ${isActive 
                  ? `${tab.activeColor} border-current` 
                  : `border-transparent text-gray-500 dark:text-gray-400 ${tab.hoverColor} dark:hover:text-gray-300`
                }
              `}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon 
                className={`
                  -ml-0.5 mr-2 h-5 w-5 transition-colors duration-200
                  ${isActive ? tab.iconColor : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'}
                `} 
              />
              <span>{tab.label}</span>
              <Badge 
                variant="secondary" 
                className={`
                  ml-2 transition-colors duration-200
                  ${isActive 
                    ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' 
                    : 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  }
                `}
              >
                {tab.count}
              </Badge>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default TaskStatusTabs;